import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Upload, Download, Search, ChevronLeft, ChevronRight, X, Layers } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PanelsList() {
  const token = localStorage.getItem('vendor_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const fileRef = useRef(null);

  const [panels, setPanels] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [processingLabs, setProcessingLabs] = useState([]);
  const [selected, setSelected] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editPanel, setEditPanel] = useState(null);
  const [form, setForm] = useState({ name: '', panel_type: 'panel', price: '', b2b_price: '', processing_lab: '', included_tests: '', b2b_prices: [] });

  const fetchPanels = useCallback(async () => {
    const params = new URLSearchParams({ page, limit: 50 });
    if (search) params.append('search', search);
    if (typeFilter) params.append('panel_type', typeFilter);
    const res = await fetch(`${API}/api/lis/panels?${params}`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    setPanels(data.panels || []);
    setTotal(data.total || 0);
    setPages(data.pages || 0);
  }, [page, search, typeFilter, token]);

  const fetchLabs = useCallback(async () => {
    const res = await fetch(`${API}/api/lis/tests/processing-labs`, { headers: { Authorization: `Bearer ${token}` } });
    setProcessingLabs(await res.json());
  }, [token]);

  useEffect(() => { fetchPanels(); }, [fetchPanels]);
  useEffect(() => { fetchLabs(); }, [fetchLabs]);

  const openCreate = () => { setEditPanel(null); setForm({ name: '', panel_type: 'panel', price: '', b2b_price: '', processing_lab: '', included_tests: '', b2b_prices: [] }); setShowDialog(true); };
  const openEdit = (p) => {
    setEditPanel(p);
    setForm({ name: p.name, panel_type: p.panel_type || 'panel', price: p.price, b2b_price: p.b2b_price, processing_lab: p.processing_lab || '', included_tests: (p.included_tests || []).join(', '), b2b_prices: p.b2b_prices || [] });
    setShowDialog(true);
  };

  const save = async () => {
    const body = { ...form, price: parseFloat(form.price) || 0, b2b_price: parseFloat(form.b2b_price) || 0, included_tests: form.included_tests ? form.included_tests.split(',').map(t => t.trim()).filter(Boolean) : [] };
    const url = editPanel ? `${API}/api/lis/panels/${editPanel.id}` : `${API}/api/lis/panels`;
    const res = await fetch(url, { method: editPanel ? 'PUT' : 'POST', headers, body: JSON.stringify(body) });
    if (res.ok) { toast.success(editPanel ? 'Updated' : 'Created'); setShowDialog(false); fetchPanels(); }
    else toast.error('Failed');
  };

  const deletePanel = async (id) => { if (!window.confirm('Delete?')) return; await fetch(`${API}/api/lis/panels/${id}`, { method: 'DELETE', headers }); toast.success('Deleted'); fetchPanels(); };
  const bulkDelete = async () => { if (!window.confirm(`Delete ${selected.length}?`)) return; await fetch(`${API}/api/lis/panels/bulk-delete`, { method: 'POST', headers, body: JSON.stringify({ ids: selected }) }); setSelected([]); fetchPanels(); };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    const res = await fetch(`${API}/api/lis/panels/bulk-upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    const data = await res.json(); toast.success(`Uploaded ${data.created} panels`); fetchPanels(); e.target.value = '';
  };

  const addB2B = () => setForm(f => ({ ...f, b2b_prices: [...f.b2b_prices, { lab: '', price: '' }] }));
  const removeB2B = (i) => setForm(f => ({ ...f, b2b_prices: f.b2b_prices.filter((_, j) => j !== i) }));
  const updateB2B = (i, field, val) => setForm(f => { const a = [...f.b2b_prices]; a[i] = { ...a[i], [field]: field === 'price' ? parseFloat(val) || 0 : val }; return { ...f, b2b_prices: a }; });

  return (
    <div data-testid="lis-panels-list">
      <div className="flex items-center justify-between mb-4">
        <div><h1 className="text-xl font-bold text-slate-800">Panels & Packages</h1><p className="text-sm text-slate-500">{total} items</p></div>
        <div className="flex gap-2">
          <input type="file" ref={fileRef} accept=".csv" onChange={handleCSVUpload} className="hidden" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}><Upload className="w-3.5 h-3.5 mr-1" /> CSV</Button>
          <Button size="sm" onClick={openCreate} data-testid="add-panel-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Panel</Button>
        </div>
      </div>

      <Card className="mb-4 border-slate-200"><CardContent className="p-3">
        <div className="flex gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]"><Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" /><Input placeholder="Search..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9" /></div>
          <Select value={typeFilter} onValueChange={v => { setTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[150px] h-9"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="panel">Panels</SelectItem><SelectItem value="package">Packages</SelectItem></SelectContent>
          </Select>
          {selected.length > 0 && <Button variant="destructive" size="sm" onClick={bulkDelete}><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete {selected.length}</Button>}
        </div>
      </CardContent></Card>

      <Card className="border-slate-200"><div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b bg-slate-50">
        <th className="p-3 w-10"><input type="checkbox" checked={selected.length === panels.length && panels.length > 0} onChange={() => selected.length === panels.length ? setSelected([]) : setSelected(panels.map(p => p.id))} /></th>
        <th className="p-3 text-left font-medium text-slate-600">Name</th><th className="p-3 text-left font-medium text-slate-600">Type</th>
        <th className="p-3 text-left font-medium text-slate-600">Tests</th><th className="p-3 text-right font-medium text-slate-600">MRP</th>
        <th className="p-3 text-right font-medium text-slate-600">B2B</th><th className="p-3 text-right font-medium text-slate-600 w-20">Actions</th>
      </tr></thead><tbody>
        {panels.map(p => (
          <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
            <td className="p-3"><input type="checkbox" checked={selected.includes(p.id)} onChange={() => setSelected(s => s.includes(p.id) ? s.filter(x => x !== p.id) : [...s, p.id])} /></td>
            <td className="p-3 font-medium text-slate-800">{p.name}</td>
            <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${p.panel_type === 'package' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{p.panel_type}</span></td>
            <td className="p-3 text-xs text-slate-500">{(p.included_tests || []).join(', ') || '-'}</td>
            <td className="p-3 text-right">Rs.{(p.price || 0).toLocaleString()}</td>
            <td className="p-3 text-right text-blue-700">Rs.{(p.b2b_price || 0).toLocaleString()}</td>
            <td className="p-3 text-right"><div className="flex gap-1 justify-end">
              <button onClick={() => openEdit(p)} className="p-1 text-slate-400 hover:text-blue-600"><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={() => deletePanel(p.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div></td>
          </tr>
        ))}
        {panels.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">No panels. Add or upload.</td></tr>}
      </tbody></table></div>
      {pages > 1 && <div className="flex items-center justify-between p-3 border-t"><span className="text-xs text-slate-500">Page {page}/{pages}</span><div className="flex gap-1"><Button variant="outline" size="sm" disabled={page<=1} onClick={()=>setPage(p=>p-1)}><ChevronLeft className="w-4 h-4" /></Button><Button variant="outline" size="sm" disabled={page>=pages} onClick={()=>setPage(p=>p+1)}><ChevronRight className="w-4 h-4" /></Button></div></div>}
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}><DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editPanel ? 'Edit Panel' : 'Add Panel'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Panel/Package Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="panel-name-input" />
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.panel_type} onValueChange={v => setForm(f => ({ ...f, panel_type: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="panel">Panel</SelectItem><SelectItem value="package">Package</SelectItem></SelectContent></Select>
            <Select value={form.processing_lab} onValueChange={v => setForm(f => ({ ...f, processing_lab: v }))}><SelectTrigger><SelectValue placeholder="Processing Lab" /></SelectTrigger><SelectContent>{processingLabs.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input type="number" placeholder="MRP" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
            <Input type="number" placeholder="B2B Price" value={form.b2b_price} onChange={e => setForm(f => ({ ...f, b2b_price: e.target.value }))} />
          </div>
          <Input placeholder="Included Tests (comma separated)" value={form.included_tests} onChange={e => setForm(f => ({ ...f, included_tests: e.target.value }))} />
          <div className="border border-slate-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2"><span className="text-sm font-medium text-slate-700">Lab-wise B2B Prices</span><Button variant="outline" size="sm" onClick={addB2B}><Plus className="w-3 h-3 mr-1" /> Add</Button></div>
            {form.b2b_prices.map((bp, i) => (
              <div key={`el-${i}`} className="flex gap-2 mb-2 items-center">
                <Select value={bp.lab} onValueChange={v => updateB2B(i, 'lab', v)}><SelectTrigger className="flex-1"><SelectValue placeholder="Lab" /></SelectTrigger><SelectContent>{processingLabs.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select>
                <Input type="number" placeholder="Price" value={bp.price} onChange={e => updateB2B(i, 'price', e.target.value)} className="w-28" />
                <button onClick={() => removeB2B(i)} className="text-red-500"><X className="w-4 h-4" /></button>
              </div>
            ))}
            {form.b2b_prices.length === 0 && <p className="text-xs text-slate-400">No lab-specific prices.</p>}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button><Button onClick={save} data-testid="save-panel-btn">{editPanel ? 'Update' : 'Create'}</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
